/**
 * Gaming Servers Management - Enterprise Dashboard
 * Gestione completa server gaming con RCON, monitoring e controllo real-time
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '@/contexts/AuthContext'
import { motion } from 'framer-motion'
import { AdminLayout } from '../../layout/AdminLayout'
import { EnterprisePageHeader } from '../../components/layout/EnterprisePageHeader'
import { EnterpriseEmptyState } from '../../components/layout/EnterpriseEmptyState'
import { EnterpriseSectionSkeleton } from '../../components/layout/EnterpriseSectionSkeleton'
import { EnterpriseStickyActionBar } from '../../components/layout/EnterpriseStickyActionBar'
import { useServersManagement } from '@/hooks/useServersManagement'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Progress } from '@/shared/components/ui/progress'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { toast } from '@/shared/hooks/use-toast'
import { 
  Gamepad2, 
  Activity, 
  Users, 
  Save,
  Terminal,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  MessageSquare,
  UserX,
  Ban,
  Send,
  RotateCcw
} from 'lucide-react'

export default function ServersManagement() {
  const navigate = useNavigate()
  const { user, logout } = useAuthContext()
  const {
    servers,
    selectedServer,
    serverPlayers,
    serverMetrics,
    actions,
    isLoading,
    error,
    selectServer,
    refresh,
    executeRCONCommand,
    kickPlayer,
    banPlayer,
    broadcastMessage,
    saveServer
  } = useServersManagement()

  const [rconCommand, setRconCommand] = useState('')
  const [broadcastMsg, setBroadcastMsg] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const handleLogout = () => {
    void logout()
  }

  const handleRCONCommand = async () => {
    if (!selectedServer || !rconCommand.trim()) return
    
    setActionLoading('rcon')
    try {
      const result = await executeRCONCommand(selectedServer.id, { command: rconCommand.trim() })
      toast({
        title: result?.success === false ? 'Comando RCON fallito' : 'Comando RCON inviato',
        description: result?.success === false
          ? result.error || 'Errore sconosciuto'
          : `Comando eseguito su ${selectedServer.name}.`,
        variant: result?.success === false ? 'destructive' : 'default',
      })
      setRconCommand('')
    } finally {
      setActionLoading(null)
    }
  }

  const handleBroadcast = async () => {
    if (!selectedServer || !broadcastMsg.trim()) return
    
    setActionLoading('broadcast')
    try {
      const result = await broadcastMessage(selectedServer.id, broadcastMsg.trim())
      toast({
        title: result?.success === false ? 'Broadcast fallito' : 'Broadcast inviato',
        description: result?.success === false
          ? result.error || 'Errore sconosciuto'
          : 'Messaggio inviato a tutti i giocatori online.',
        variant: result?.success === false ? 'destructive' : 'default',
      })
      setBroadcastMsg('')
    } finally {
      setActionLoading(null)
    }
  }

  const handlePlayerAction = async (playerName: string, action: 'kick' | 'ban') => {
    if (!selectedServer) return
    
    setActionLoading(`${action}-${playerName}`)
    try {
      if (action === 'kick') {
        const result = await kickPlayer(selectedServer.id, { player_name: playerName, reason: 'Admin action' })
        toast({
          title: result?.success === false ? 'Kick fallito' : 'Giocatore espulso',
          description: result?.success === false ? result.error || 'Errore sconosciuto' : playerName,
          variant: result?.success === false ? 'destructive' : 'default',
        })
      } else {
        const result = await banPlayer(selectedServer.id, { player_name: playerName, reason: 'Admin action' })
        toast({
          title: result?.success === false ? 'Ban fallito' : 'Giocatore bannato',
          description: result?.success === false ? result.error || 'Errore sconosciuto' : playerName,
          variant: result?.success === false ? 'destructive' : 'default',
        })
      }
    } finally {
      setActionLoading(null)
    }
  }

  const handleSaveServer = async () => {
    if (!selectedServer) return
    
    setActionLoading('save')
    try {
      const result = await saveServer(selectedServer.id)
      toast({
        title: result?.success === false ? 'Save fallito' : 'Save completato',
        description: result?.success === false
          ? result.error || 'Errore sconosciuto'
          : `World state salvato su ${selectedServer.name}.`,
        variant: result?.success === false ? 'destructive' : 'default',
      })
    } finally {
      setActionLoading(null)
    }
  }
  const onlineServers = servers.filter(s => s.status === 'online').length
  const totalPlayers = servers.reduce((sum, s) => sum + (s.players?.known === false ? 0 : s.players.current), 0)
  const maxPlayers = servers.reduce((sum, s) => sum + s.players.max, 0)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-500'
      case 'offline': return 'text-red-500'
      case 'starting': return 'text-yellow-500'
      case 'stopping': return 'text-orange-500'
      default: return 'text-gray-500'
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'online': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Online</Badge>
      case 'offline': return <Badge variant="destructive">Offline</Badge>
      case 'starting': return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Starting</Badge>
      case 'stopping': return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">Stopping</Badge>
      default: return <Badge variant="outline">Unknown</Badge>
    }
  }

  const getGameIcon = (game: string) => {
    switch (game.toLowerCase()) {
      case 'minecraft': return '🟫'
      case 'rust': return '🦀'
      case 'rimworld': return '🌍'
      case 'ksp': return '🚀'
      default: return '🎮'
    }
  }

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6 sm:space-y-8">
        <EnterprisePageHeader
          badge="Servers"
          title="Gaming Servers Management"
          subtitle="Gestione e controllo completo server gaming"
          icon={<Gamepad2 className="h-6 w-6" />}
          isLoading={isLoading}
          onRefresh={refresh}
        />

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 sm:gap-6">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gamepad2 className="h-4 w-4 text-blue-500" />
                  Server Totali
                </div>
                <Badge variant="outline" className="text-xs">
                  Live
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">{servers.length}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {onlineServers} online, {servers.length - onlineServers} offline
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-500" />
                  Giocatori Attivi
                </div>
                <Badge variant="outline" className="text-xs">
                  Real-time
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">{totalPlayers}</div>
              <div className="text-sm text-muted-foreground mt-1">
                su {maxPlayers} slot totali
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-orange-500" />
                  Performance
                </div>
                <Badge variant="default" className="text-xs">
                  Ottimale
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">
                {onlineServers > 0 ? Math.round((onlineServers / servers.length) * 100) : 0}%
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Uptime medio
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {/* Server List */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gamepad2 className="h-5 w-5 text-primary" />
                Server Gaming
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <EnterpriseSectionSkeleton rows={5} showHeader={false} rowClassName="h-24" />
              ) : servers.length > 0 ? (
                <div className="space-y-3">
                  {servers.map((server) => (
                    <motion.div
                      key={server.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                        selectedServer?.id === server.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        const sid = String(server.id || '').toLowerCase()
                        const game = String(server.game || '').toLowerCase()
                        if (sid === 'zomboid' || game === 'zomboid') {
                          navigate('/admin/zomboid')
                          return
                        }
                        selectServer(server)
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getGameIcon(server.game)}</span>
                          <div>
                            <h3 className="font-semibold">{server.name}</h3>
                            <p className="text-sm text-muted-foreground">{server.game}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(server.status)}
                          <p className="text-sm text-muted-foreground mt-1">
                            {server.players?.known === false ? (
                              <>N/A</>
                            ) : (
                              <>{server.players.current}/{server.players.max} giocatori</>
                            )}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EnterpriseEmptyState
                  icon={<Gamepad2 className="h-10 w-10 opacity-50" />}
                  title="Nessun server disponibile"
                  description="Il backend non ha restituito server monitorabili."
                />
              )}
            </CardContent>
          </Card>

          {/* Server Details */}
          {selectedServer ? (
            <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getGameIcon(selectedServer.game)}</span>
                    {selectedServer.name}
                  </div>
                  {getStatusBadge(selectedServer.status)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4">
                    <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
                    <TabsTrigger value="players" className="text-xs sm:text-sm">Players</TabsTrigger>
                    <TabsTrigger value="rcon" className="text-xs sm:text-sm">RCON</TabsTrigger>
                    <TabsTrigger value="actions" className="text-xs sm:text-sm">Actions</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">IP Address</p>
                        <p className="font-mono">{selectedServer.ip}:{selectedServer.port}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <p className={getStatusColor(selectedServer.status)}>{selectedServer.status}</p>
                      </div>
                    </div>

                    {serverMetrics && typeof serverMetrics.cpu_percent === 'number' && (
                      <div className="space-y-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          Performance Metrics
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>CPU</span>
                              <span>{(serverMetrics.cpu_percent ?? 0).toFixed(1)}%</span>
                            </div>
                            <Progress value={Math.min(serverMetrics.cpu_percent ?? 0, 100)} className="h-2" />
                          </div>
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Memory</span>
                                  <span>{(serverMetrics.memory_mb ?? 0).toFixed(0)} MB ({(serverMetrics.memory_percent ?? 0).toFixed(1)}%)</span>
                                </div>
                                <Progress value={Math.min(serverMetrics.memory_percent ?? 0, 100)} className="h-2" />
                              </div>
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Network</span>
                                  <span>
                                    {(typeof serverMetrics.network_in === "number" ? serverMetrics.network_in.toFixed(1) : "n/a")} MB in
                                    {" / "}
                                    {(typeof serverMetrics.network_out === "number" ? serverMetrics.network_out.toFixed(1) : "n/a")} MB out
                                  </span>
                                </div>
                              </div>
                              {serverMetrics.uptime ? (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Uptime</span>
                                  <span className="font-mono">{serverMetrics.uptime}</span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </TabsContent>

                  <TabsContent value="players" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Giocatori Online ({serverPlayers.length})
                      </h4>
                    </div>
                    <ScrollArea className="h-48">
                      {serverPlayers.length > 0 ? (
                        <div className="space-y-2">
                          {serverPlayers.map((player, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                              <span className="font-medium">{player.name}</span>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePlayerAction(player.name, 'kick')}
                                  disabled={actionLoading === `kick-${player.name}`}
                                >
                                  <UserX className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handlePlayerAction(player.name, 'ban')}
                                  disabled={actionLoading === `ban-${player.name}`}
                                >
                                  <Ban className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EnterpriseEmptyState
                          icon={<Users className="h-8 w-8 opacity-50" />}
                          title="Nessun giocatore online"
                          description="La lista verra aggiornata automaticamente al prossimo polling."
                        />
                      )}
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="rcon" className="space-y-4">
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Terminal className="h-4 w-4" />
                        RCON Console
                      </h4>
                      <EnterpriseStickyActionBar position="top">
                        <Input
                          placeholder="Inserisci comando RCON..."
                          value={rconCommand}
                          onChange={(e) => setRconCommand(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleRCONCommand()}
                          className="min-w-[220px] flex-1"
                        />
                        <Button 
                          onClick={handleRCONCommand}
                          disabled={!rconCommand.trim() || actionLoading === 'rcon'}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </EnterpriseStickyActionBar>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Broadcast Message
                      </h4>
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Messaggio da inviare a tutti i giocatori..."
                          value={broadcastMsg}
                          onChange={(e) => setBroadcastMsg(e.target.value)}
                          rows={3}
                        />
                        <Button 
                          onClick={handleBroadcast}
                          disabled={!broadcastMsg.trim() || actionLoading === 'broadcast'}
                          className="w-full"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Invia Messaggio
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="actions" className="space-y-4">
                    <EnterpriseStickyActionBar position="top" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Button
                        onClick={handleSaveServer}
                        disabled={actionLoading === 'save'}
                        variant="outline"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save World
                      </Button>
                      <Button
                        disabled={true}
                        variant="outline"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restart
                      </Button>
                    </EnterpriseStickyActionBar>

                    <div className="space-y-2">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Recent Actions
                      </h4>
                      <ScrollArea className="h-32">
                        {actions.length > 0 ? (
                          <div className="space-y-1">
                            {actions.map((action) => (
                              <div key={action.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm">
                                {action.status === 'completed' ? (
                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                ) : (
                                  <AlertCircle className="h-3 w-3 text-red-500" />
                                )}
                                <span className="flex-1">{action.message}</span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(action.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <EnterpriseEmptyState
                            icon={<Clock className="h-6 w-6 opacity-50" />}
                            title="Nessuna azione recente"
                            description="Le ultime azioni runtime appariranno qui."
                            className="min-h-24"
                          />
                        )}
                      </ScrollArea>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
              <CardContent className="flex items-center justify-center h-72 sm:h-96">
                <EnterpriseEmptyState
                  icon={<Gamepad2 className="h-12 w-12 opacity-50" />}
                  title="Seleziona un server"
                  description="Clicca su un server per visualizzare dettagli, RCON e azioni operative."
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Errore di connessione</span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
